import classNames from "classnames";
import React from "react";
import { Svg, SvgSize } from "../../../../Shared/Assets/Svg/React";
import { Button, ButtonColor, ButtonSize } from "../../../../Shared/Buttons/React";
import { ErrorMessage } from "../../components/shared/errorMessage";
import LoadingStatus from "../../components/shared/loadingStatus";
import Spinner from "../../components/shared/spinner";
import { useTranslationContext } from "../../context";
import { AppointmentTime } from "../../types";
import { formatAppointmentTime } from "../../utilities";

interface AppointmentTimeListProps {
  loading: boolean;
  /** The times failed to load: shown with "Try again" instead of "no available times" */
  loadError?: Error | null;
  onRetry?: () => void;
  appointmentTimes: AppointmentTime[];
  onSelectTime: (appointmentTime: AppointmentTime) => void;
  errorSelectTimeNoLongerAvailable: boolean;
  noAvailableTimesMessage?: string;
  firstAvailableDate?: Date;
  onSelectFirstAvailableDate?: () => void;
  firstAvailableTimeLabel?: string;
}

export const AppointmentTimeList = ({
  loading,
  loadError,
  onRetry,
  appointmentTimes,
  onSelectTime,
  errorSelectTimeNoLongerAvailable,
  noAvailableTimesMessage,
  firstAvailableDate,
  onSelectFirstAvailableDate,
  firstAvailableTimeLabel
}: AppointmentTimeListProps) => {
  const translationContext = useTranslationContext();

  return (
    <div
      className={classNames(
        "flex flex-wrap gap-xs sm:gap-md pt-lg sm:pt-xl border-t border-neutral-100",
        {
          "justify-center": loading
        }
      )}
    >
      {loading ? (
        <LoadingStatus
          loading={loading}
          message={translationContext?.translations?.loadingAvailableTimes}
        >
          <Spinner visible={loading} />
        </LoadingStatus>
      ) : loadError ? (
        <ErrorMessage error={loadError} onRetry={onRetry} />
      ) : (
        <div
          className="flex flex-wrap gap-xs sm:gap-md"
          data-online-booking-calendar-timeslot-list
        >
          {appointmentTimes && appointmentTimes.length > 0 ? (
            appointmentTimes.map((bookableTime, index) => (
              <button
                key={index}
                onClick={() => onSelectTime(bookableTime)}
                className={classNames(
                  "w-[4.825rem] h-[2.5rem] sm:w-[4.5rem] sm:h-[3.75rem]",
                  "font-guardian text-t-sm text-center bg-turquoise-200 hover:bg-turquoise-600",
                  "focus-visible:bg-turquoise-600 text-dark-blue rounded-tl-br-sm transition-short-in-out",
                  "focus-outline"
                )}
                aria-label={(() => {
                  const appointmentDate = new Date(bookableTime.availableTime || "");
                  const timeStr = formatAppointmentTime(appointmentDate.toString());
                  return `${translationContext?.translations?.selectTime} ${timeStr}`;
                })()}
                data-online-booking-calendar-timeslot
              >
                {(() => {
                  const appointmentDate = new Date(bookableTime.availableTime || "");
                  return formatAppointmentTime(appointmentDate.toString());
                })()}
              </button>
            ))
          ) : (
            <div className="flex flex-wrap items-center gap-sm">
              <div
                className="flex items-center gap-xs p-md rounded-tl-br-sm bg-yellow-600 text-t-yellow"
                role="status"
                aria-live="polite"
              >
                <Svg href="/svg/system/info.svg" size={SvgSize.MD} />
                {noAvailableTimesMessage ||
                  translationContext?.translations?.noAvailableTimes}
              </div>

              {firstAvailableDate && onSelectFirstAvailableDate && (
                <Button
                  size={ButtonSize.MD}
                  color={ButtonColor.Turquoise}
                  onClick={onSelectFirstAvailableDate}
                >
                  {firstAvailableTimeLabel || "Go to first available time"}
                </Button>
              )}
            </div>
          )}

          {errorSelectTimeNoLongerAvailable && (
            <div
              className="p-md mb-md rounded-tl-br-sm bg-yellow-600 text-dark-blue"
              role="alert"
              aria-live="assertive"
            >
              {translationContext?.translations?.timeNoLongerAvailable}
            </div>
          )}
        </div>
      )}
    </div>
  );
};
